import { useEffect, useState } from 'react';
import type {
  DeepPartialSkipArrayKey,
  FieldValues,
  Path,
  Resolver,
  UseFormReturn,
} from 'react-hook-form';
import { useWatch } from 'react-hook-form';

export type UseCustomFormReturn<TFieldValues extends FieldValues = FieldValues> = {
  form: UseFormReturn<TFieldValues>;
  customState: {
    focusedField: Path<TFieldValues> | null;
    onFocus: (field: Path<TFieldValues>) => void;
    onBlur: () => void;
    isCompleted: boolean;
  };
};
export const useCustomForm = <TFieldValues extends FieldValues = FieldValues>(
  form: UseFormReturn<TFieldValues, unknown>,
  resolver: Resolver<DeepPartialSkipArrayKey<TFieldValues>, unknown>,
): UseCustomFormReturn<TFieldValues> => {
  const [focusedField, setFocusedField] = useState<Path<TFieldValues> | null>(null);
  const [isCompleted, toggleIsCompleted] = useState(false);

  const onFocus = (field: Path<TFieldValues>) => {
    setFocusedField(field);
  };
  const onBlur = () => {
    if (focusedField === 'email') {
      form.trigger(focusedField);
    }
    setFocusedField(null);
  };

  const values = useWatch<TFieldValues>({ control: form.control });

  useEffect(() => {
    const promise = resolver(values, undefined, {
      fields: {},
      shouldUseNativeValidation: undefined,
    });

    if (promise instanceof Promise) {
      promise.then((val) => {
        toggleIsCompleted(!Object.keys(val.errors).length);
      });
    }
  }, [values]);

  return {
    form,
    customState: {
      focusedField,
      onFocus,
      onBlur,
      isCompleted,
    },
  };
};
